import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

/**
 * V DEV vypíše JSON/text z GoTrue pri 401 na /auth/v1/token (PKCE výmena).
 * Bez toho vidíš len „Unauthorized“ bez konkrétneho dôvodu.
 */
function wrapFetchForAuthTokenDebug(baseFetch: typeof fetch): typeof fetch {
  return async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (!import.meta.env.DEV) {
      return response;
    }
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    if (url.includes("/auth/v1/token") && response.status === 401) {
      try {
        const copy = response.clone();
        const text = await copy.text();
        let parsed: unknown = text;
        try {
          parsed = JSON.parse(text) as unknown;
        } catch {
          /* nechať raw text */
        }
        console.error("[oauth-debug] GoTrue POST /auth/v1/token 401:", parsed);
      } catch (e) {
        console.error("[oauth-debug] Nepodarilo sa prečítať telo 401:", e);
      }
    }
    return response;
  };
}

// Disable WebSocket connections for Realtime if Realtime is disabled
// This prevents WebSocket errors in console when Realtime is not available
if (typeof window !== "undefined") {
  const isSelfHostedCheck =
    supabaseUrl.includes("ayocruz.com") &&
    !supabaseUrl.includes(".supabase.co");
  const shouldDisableRealtime =
    isSelfHostedCheck || import.meta.env.VITE_DISABLE_REALTIME === "true";

  if (shouldDisableRealtime) {
    const originalWebSocket = window.WebSocket;

    // @ts-expect-error - Monkey patch WebSocket to suppress errors for Realtime
    window.WebSocket = class extends originalWebSocket {
      constructor(url: string | URL, protocols?: string | string[]) {
        const urlString = typeof url === "string" ? url : url.toString();
        // Check if this is a Supabase Realtime WebSocket connection
        if (urlString.includes("/realtime/")) {
          // Create WebSocket but suppress all errors and close immediately
          try {
            super(url, protocols);
            // Close immediately to prevent connection attempts
            // Use setTimeout to ensure WebSocket is created first
            setTimeout(() => {
              try {
                if (
                  this.readyState === WebSocket.CONNECTING ||
                  this.readyState === WebSocket.OPEN
                ) {
                  this.close();
                }
              } catch {
                // Ignore close errors
              }
            }, 0);
          } catch {
            // Silently fail - Realtime is not available
            // Return a dummy object that looks like WebSocket
            // @ts-expect-error - Create a no-op WebSocket
            return {
              readyState: 3, // CLOSED
              close: () => {},
              send: () => {},
              addEventListener: () => {},
              removeEventListener: () => {},
              dispatchEvent: () => true,
            };
          }
        } else {
          // Normal WebSocket for non-Realtime connections
          super(url, protocols);
        }
      }
    } as typeof WebSocket;
  }
}

export const isSupabaseConfigured = Boolean(
  supabaseUrl &&
    supabaseAnonKey &&
    supabaseUrl.trim() !== "" &&
    supabaseAnonKey.trim() !== "",
);

// Option to disable Realtime via environment variable
// Set VITE_DISABLE_REALTIME=true to disable Realtime subscriptions
// Realtime is automatically disabled for self-hosted instances (non-.supabase.co domains)
const isSelfHosted =
  isSupabaseConfigured &&
  supabaseUrl.includes("ayocruz.com") &&
  !supabaseUrl.includes(".supabase.co");

export const isRealtimeEnabled =
  isSupabaseConfigured &&
  import.meta.env.VITE_DISABLE_REALTIME !== "true" &&
  !isSelfHosted;

/**
 * Kľúč localStorage pre PKCE (must match Supabase JS default: sb-<first-label>-auth-token).
 * Pri vlastnej doméne (napr. supabase.ayocruz.com) je to typicky `sb-supabase-auth-token`.
 * Voliteľne: VITE_SUPABASE_AUTH_STORAGE_KEY ak máš viac frontendov na rovnakom origin-e.
 */
export function getSupabaseAuthStorageKey(): string {
  const custom = import.meta.env.VITE_SUPABASE_AUTH_STORAGE_KEY?.trim();
  if (custom) {
    return custom;
  }
  if (!supabaseUrl) {
    return "sb-auth-token";
  }
  try {
    const hostFirstLabel = new URL(supabaseUrl).hostname.split(".")[0];
    return `sb-${hostFirstLabel}-auth-token`;
  } catch {
    return "sb-auth-token";
  }
}

if (!isSupabaseConfigured) {
  console.warn(
    "⚠️ Supabase not configured. App will run in demo mode without authentication. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env file",
  );
} else if (!isRealtimeEnabled && import.meta.env.DEV) {
  if (isSelfHosted) {
    console.info(
      "ℹ️ Supabase Realtime is disabled (self-hosted instance detected). The app will work without real-time updates.",
    );
  } else if (import.meta.env.VITE_DISABLE_REALTIME === "true") {
    console.info(
      "ℹ️ Supabase Realtime is disabled (VITE_DISABLE_REALTIME=true). The app will work without real-time updates.",
    );
  }
}

/**
 * Supabase client instance
 * Singleton pattern for consistent usage across the app
 *
 * Note: If Supabase is not configured, this will create a client that will fail gracefully
 */
const authStorageKey = getSupabaseAuthStorageKey();
const browserLocalStorage =
  typeof window !== "undefined" ? window.localStorage : undefined;

const devDebugFetch =
  import.meta.env.DEV && typeof globalThis.fetch === "function"
    ? wrapFetchForAuthTokenDebug(globalThis.fetch.bind(globalThis))
    : undefined;

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storageKey: authStorageKey,
        // Explicit localStorage: zabráni edge prípadom, kde default storage nie je perzistentný.
        storage: browserLocalStorage,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: "pkce",
      },
      global: {
        headers: {
          "X-Client-Info": "aac-app",
        },
        ...(devDebugFetch ? { fetch: devDebugFetch } : {}),
      },
      // Disable Realtime if not enabled - prevents WebSocket connection attempts
      realtime: isRealtimeEnabled
        ? undefined
        : {
            // Disable Realtime by setting params to prevent WebSocket connections
            params: {
              eventsPerSecond: 0,
            },
          },
    })
  : createClient(
      "https://placeholder.supabase.co",
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0",
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      },
    );
